import { useState, useEffect, useRef, useCallback } from 'react';
import { FiasWebSocket, isBenignConnectAbort } from '../websocket';
import { authApi } from '../auth-api';
import { notifySessionExpired } from '../auth-refresh';

/**
 * Configuration options for the useStreaming hook
 */
export interface UseStreamingOptions {
  /** Request ID to subscribe to */
  requestId: string | null;
  /** JWT auth token for WebSocket connection */
  authToken: string | null;
  /** WebSocket endpoint URL */
  endpoint: string | null;
  /** Whether streaming is enabled (default: true) */
  enabled?: boolean;
  /** Callback when streaming text updates */
  onTextUpdate?: (nodeId: string, text: string) => void;
  /** Callback when thinking text updates (for models with extended thinking) */
  onThinkingUpdate?: (nodeId: string, text: string) => void;
  /** Callback when a node completes */
  onNodeComplete?: (nodeId: string) => void;
  /** Callback when the entire request completes (payload contains extra fields from the complete event) */
  onComplete?: (requestId: string, payload?: Record<string, unknown>) => void;
  /** Callback when an error occurs */
  onError?: (code: string, message: string) => void;
  /** Callback when progress updates (for image generation) */
  onProgress?: (stage: string, message: string) => void;
  /** Callback when image generation completes */
  onImageComplete?: (data: {
    requestId: string;
    imageRef: string;
    fileId?: string;
    revisedPrompt?: string;
    provider: string;
    model: string;
  }) => void;
  /** Callback when UI definition is updated (for builder) */
  onUiUpdate?: (uiDefinition: unknown) => void;
  /** Callback when session settings are updated (e.g. guided phase changes) */
  onSettingsUpdate?: (settings: Record<string, unknown>) => void;
  /** Callback when files are updated (from plugin builder tools) */
  onFilesUpdate?: (files: Record<string, unknown>) => void;
  /** Callback when system prompt is received (verbose mode) */
  onSystemPrompt?: (prompt: string) => void;
  /** Callback when a tool is called (verbose mode) */
  onToolCall?: (toolName: string, toolInput: unknown) => void;
  /** Callback when a tool result is received (verbose mode) */
  onToolResult?: (toolName: string, toolResult: unknown, isError: boolean) => void;
  /** Callback when model info is received (verbose mode) */
  onModelInfo?: (model: string, intent: string) => void;
  /** Callback when tools available list is received (verbose mode) */
  onToolsAvailable?: (tools: Array<{ name: string; description: string }>) => void;
  /** Callback when an arche proposal is received (Fias AI arche integration) */
  onArcheProposal?: (proposal: {
    proposalId: string;
    actions: Array<{ actionId: string; params: Record<string, unknown>; description: string }>;
  }) => void;
  /** Callback when search results are received (Fias AI) */
  onSearchResults?: (results: unknown[], query: string) => void;
}

/**
 * Result returned by the useStreaming hook
 */
export interface UseStreamingResult {
  /** Current connection state */
  connectionState: 'disconnected' | 'connecting' | 'connected' | 'reconnecting';
  /** Whether currently connected */
  isConnected: boolean;
  /** Whether subscribed to a request */
  isSubscribed: boolean;
  /** Accumulated text for each node */
  streamingText: Record<string, string>;
  /** Accumulated thinking text for each node (for models with extended thinking) */
  streamingThinking: Record<string, string>;
  /** Set of completed node IDs */
  completedNodes: Set<string>;
  /** Whether the entire request has completed */
  isComplete: boolean;
  /** Payload from the complete event (if any) */
  completePayload: Record<string, unknown> | null;
  /** Last error (if any) */
  error: { code: string; message: string } | null;
  /** Manually connect to WebSocket */
  connect: () => Promise<void>;
  /** Manually disconnect from WebSocket */
  disconnect: () => void;
  /** Reset streaming state (clear text and completions) */
  reset: () => void;
}

/**
 * React hook for managing WebSocket streaming connections
 *
 * Automatically connects when authToken and endpoint are provided,
 * and subscribes to the requestId when it changes.
 *
 * @example
 * ```tsx
 * function StreamingComponent({ requestId }: { requestId: string }) {
 *   const {
 *     connectionState,
 *     streamingText,
 *     isComplete,
 *     error,
 *   } = useStreaming({
 *     requestId,
 *     authToken: 'your-jwt-token',
 *     endpoint: 'wss://api.example.com/stream',
 *   });
 *
 *   if (error) {
 *     return <div>Error: {error.message}</div>;
 *   }
 *
 *   return (
 *     <div>
 *       {Object.entries(streamingText).map(([nodeId, text]) => (
 *         <div key={nodeId}>
 *           <h3>{nodeId}</h3>
 *           <p>{text}</p>
 *         </div>
 *       ))}
 *       {isComplete && <div>Complete!</div>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useStreaming(options: UseStreamingOptions): UseStreamingResult {
  const {
    requestId,
    authToken,
    endpoint,
    enabled = true,
    onTextUpdate,
    onThinkingUpdate,
    onNodeComplete,
    onComplete,
    onError,
    onProgress,
    onImageComplete,
    onUiUpdate,
    onSettingsUpdate,
    onFilesUpdate,
    onSystemPrompt,
    onToolCall,
    onToolResult,
    onModelInfo,
    onToolsAvailable,
    onArcheProposal,
    onSearchResults,
  } = options;

  // State
  const [connectionState, setConnectionState] = useState<
    'disconnected' | 'connecting' | 'connected' | 'reconnecting'
  >('disconnected');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [streamingText, setStreamingText] = useState<Record<string, string>>({});
  const [streamingThinking, setStreamingThinking] = useState<Record<string, string>>({});
  const [completedNodes, setCompletedNodes] = useState<Set<string>>(new Set());
  const [isComplete, setIsComplete] = useState(false);
  const [completePayload, setCompletePayload] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);

  // Refs
  const wsRef = useRef<FiasWebSocket | null>(null);
  const cleanupRef = useRef<(() => void)[]>([]);
  const lastSubscribedRequestIdRef = useRef<string | null>(null);

  // Reset state
  const reset = useCallback(() => {
    console.log('[useStreaming] reset() called', new Error().stack);
    setStreamingText({});
    setStreamingThinking({});
    setCompletedNodes(new Set());
    setIsComplete(false);
    setCompletePayload(null);
    setError(null);
    setIsSubscribed(false);
    lastSubscribedRequestIdRef.current = null;
  }, []);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    if (!authToken || !endpoint) {
      console.warn('Cannot connect: missing authToken or endpoint');
      return;
    }

    // Create new WebSocket instance if needed
    if (!wsRef.current) {
      wsRef.current = new FiasWebSocket({ endpoint });
    }

    // Pre-register the requested subscription so it dispatches the moment
    // the socket is open — `FiasWebSocket.subscribe` is now safe to call
    // before connect resolves, and the `onopen` handler auto-flushes the
    // recorded subscription. This closes a race where the React-side
    // `connectionState → useEffect → subscribe` hop dropped the call
    // because the state update wasn't observed in time.
    //
    // Gated on `lastSubscribedRequestIdRef.current === null` so it only
    // fires on the FIRST subscription. Transitions between requestIds
    // are owned by the connectionState-driven useEffect below, which
    // sends the required `unsubscribe(old)` frame before `subscribe(new)`.
    // Letting this path also fire on transitions would race that effect
    // and the unsubscribe→subscribe ordering would break.
    if (
      requestId &&
      lastSubscribedRequestIdRef.current === null &&
      wsRef.current.getCurrentSubscription() !== requestId
    ) {
      lastSubscribedRequestIdRef.current = requestId;
      wsRef.current.subscribe(requestId);
    }

    if (wsRef.current.isConnected()) {
      // Already connected — subscribe() above already sent the frame.
      return;
    }

    // Pass a ticket provider, not a static token: each (re)connect
    // mints a fresh single-use 30s ws-ticket so reconnects after a
    // network blip don't replay a consumed credential. doConnect()
    // throws 'Authentication credential unavailable' when the provider
    // returns null (session expired) — translate that to UNAUTHORIZED.
    try {
      await wsRef.current.connect(() => authApi.getWebSocketTicket());
    } catch (err) {
      // A handshake torn down by `disconnect()` or superseded by a newer
      // connect attempt is an intentional lifecycle event, not a failure to
      // reach the server — the client's reconnect path (or the next connect())
      // recovers. Surfacing it as a fatal streamingError is what made the first
      // chat prompt after a cold/cycled socket render "An error occurred with
      // this AI provider" even though the request then streamed fine.
      if (isBenignConnectAbort(err)) {
        return;
      }
      const message = err instanceof Error ? err.message : 'Failed to connect';
      const isAuthFailure = message === 'Authentication credential unavailable';
      setError({
        code: isAuthFailure ? 'UNAUTHORIZED' : 'CONNECTION_FAILED',
        message: isAuthFailure ? 'Session expired. Please log out and log back in.' : message,
      });
      // Surface the SessionExpiredListener modal so the user has a
      // one-click recovery path. The local `error` above is preserved
      // for consumers that render an inline banner.
      if (isAuthFailure) notifySessionExpired();
    }
  }, [authToken, endpoint, requestId, reset]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    wsRef.current?.disconnect();
    reset();
  }, [reset]);

  // Set up WebSocket event handlers
  useEffect(() => {
    if (!enabled || !endpoint) {
      return;
    }

    // Create WebSocket instance
    if (!wsRef.current) {
      wsRef.current = new FiasWebSocket({ endpoint });
    }

    const ws = wsRef.current;

    // Set up event handlers
    const unsubConnectionChange = ws.onConnectionChange((state) => {
      setConnectionState(state);
    });

    const unsubToken = ws.onToken((nodeId, text) => {
      setStreamingText((prev) => {
        const newText = (prev[nodeId] || '') + text;
        console.log('[useStreaming] Token received', { nodeId, newLength: newText.length });
        return {
          ...prev,
          [nodeId]: newText,
        };
      });
      onTextUpdate?.(nodeId, text);
    });

    const unsubThinking = ws.onThinking((nodeId, text) => {
      setStreamingThinking((prev) => ({
        ...prev,
        [nodeId]: (prev[nodeId] || '') + text,
      }));
      onThinkingUpdate?.(nodeId, text);
    });

    const unsubNodeComplete = ws.onNodeComplete((nodeId) => {
      setCompletedNodes((prev) => new Set(prev).add(nodeId));
      onNodeComplete?.(nodeId);
    });

    const unsubComplete = ws.onComplete((completedRequestId, payload) => {
      setIsComplete(true);
      setCompletePayload(payload ?? null);
      onComplete?.(completedRequestId, payload);
    });

    const unsubError = ws.onError((code, message) => {
      setError({ code, message });
      // INSUFFICIENT_CREDITS no longer auto-navigates from streaming. The
      // chat consumer renders a friendly inline error via formatMessageContent
      // (see Customer Support and Learn About Fias) which is less jarring
      // than a mid-conversation redirect. The HTTP interceptor in api.ts
      // still triggers fias:credits-exhausted on a 402 with
      // allBucketsExhausted=true for explicit "click and pay" actions.
      onError?.(code, message);
    });

    const unsubSubscribed = ws.onSubscribed(() => {
      setIsSubscribed(true);
    });

    const unsubProgress = ws.onProgress((stage, message) => {
      onProgress?.(stage, message);
    });

    const unsubImageComplete = ws.onImageComplete((data) => {
      onImageComplete?.(data);
    });

    const unsubUiUpdate = ws.onUiUpdate((_nodeId, uiDefinition) => {
      onUiUpdate?.(uiDefinition);
    });

    const unsubSettingsUpdate = ws.onSettingsUpdate((settings, _requestId) => {
      onSettingsUpdate?.(settings);
    });

    const unsubFilesUpdate = ws.onFilesUpdate((files, _requestId) => {
      onFilesUpdate?.(files);
    });

    const unsubSystemPrompt = ws.onSystemPrompt((prompt, _requestId) => {
      onSystemPrompt?.(prompt);
    });

    const unsubToolCall = ws.onToolCall((toolName, toolInput, _requestId) => {
      onToolCall?.(toolName, toolInput);
    });

    const unsubToolResult = ws.onToolResult((toolName, toolResult, isError, _requestId) => {
      onToolResult?.(toolName, toolResult, isError);
    });

    const unsubModelInfo = ws.onModelInfo((model, intent, _requestId) => {
      onModelInfo?.(model, intent);
    });

    const unsubToolsAvailable = ws.onToolsAvailable((tools, _requestId) => {
      onToolsAvailable?.(tools);
    });

    const unsubSearchResults = ws.onSearchResults((results, query, _requestId) => {
      onSearchResults?.(results, query);
    });

    const unsubArcheProposal = ws.onArcheProposal((proposal, _requestId) => {
      onArcheProposal?.(proposal);
    });

    // Store cleanup functions
    cleanupRef.current = [
      unsubConnectionChange,
      unsubToken,
      unsubThinking,
      unsubNodeComplete,
      unsubComplete,
      unsubError,
      unsubSubscribed,
      unsubProgress,
      unsubImageComplete,
      unsubUiUpdate,
      unsubSettingsUpdate,
      unsubFilesUpdate,
      unsubSystemPrompt,
      unsubToolCall,
      unsubToolResult,
      unsubModelInfo,
      unsubToolsAvailable,
      unsubSearchResults,
      unsubArcheProposal,
    ];

    // Cleanup on unmount
    return () => {
      cleanupRef.current.forEach((fn) => fn());
      cleanupRef.current = [];
    };
  }, [
    enabled,
    endpoint,
    onTextUpdate,
    onThinkingUpdate,
    onNodeComplete,
    onComplete,
    onError,
    onProgress,
    onImageComplete,
    onUiUpdate,
    onSettingsUpdate,
    onFilesUpdate,
    onSystemPrompt,
    onToolCall,
    onToolResult,
    onModelInfo,
    onToolsAvailable,
    onArcheProposal,
    onSearchResults,
  ]);

  // Auto-connect when the consumer is enabled and authenticated. We
  // intentionally do NOT gate on requestId here: a chat page that already
  // has the user's auth token can warm the WebSocket up-front so the first
  // submit doesn't pay the cold-start handshake cost (~1s of ws-ticket
  // mint + TLS + WS upgrade). Subsequent submits then only need to send
  // a `subscribe` over the existing socket.
  //
  // Disconnect on any of !enabled / !authToken / !endpoint so the hook
  // self-defends against a stale authenticated WS outliving its
  // credentials. The enabled-driven case covers logout via the
  // `enabled: !!authToken` consumer pattern, but checking authToken
  // directly here means the hook is safe even if a consumer forgets the
  // pattern (e.g. always-enabled streaming with rotating credentials).
  useEffect(() => {
    if (enabled && authToken && endpoint) {
      connect();
    } else if ((!enabled || !authToken || !endpoint) && wsRef.current) {
      wsRef.current.disconnect();
    }
  }, [enabled, authToken, endpoint, connect]);

  // Subscribe to requestId when it changes
  useEffect(() => {
    if (!enabled || !requestId || connectionState !== 'connected') {
      return;
    }

    // The WS protocol is one-active-request-subscription per connection
    // (websocket.ts: currentSubscription is a single string|null). When the
    // consumer moves from one requestId to another over a warm socket, we
    // must explicitly unsubscribe from the previous one or the server keeps
    // delivering its events into a connection now subscribed to something
    // else. The order is queued frames: unsubscribe → subscribe.
    if (lastSubscribedRequestIdRef.current && lastSubscribedRequestIdRef.current !== requestId) {
      wsRef.current?.unsubscribe();
    }

    // Only reset local state for a NEW requestId, not a reconnection to the
    // same one (the server-side pending-state replay will re-deliver if the
    // request had completed during the disconnect).
    if (lastSubscribedRequestIdRef.current !== requestId) {
      reset();
      lastSubscribedRequestIdRef.current = requestId;
    }

    wsRef.current?.subscribe(requestId);
  }, [enabled, requestId, connectionState, reset]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      wsRef.current?.disconnect();
      wsRef.current = null;
    };
  }, []);

  return {
    connectionState,
    isConnected: connectionState === 'connected',
    isSubscribed,
    streamingText,
    streamingThinking,
    completedNodes,
    isComplete,
    completePayload,
    error,
    connect,
    disconnect,
    reset,
  };
}

export default useStreaming;
